import React, { useState, useEffect } from 'react';
import apiService, { authFetch } from '../services/api';
import './QAAssessmentModal.css';

export interface QAQuestion {
  questionId: string;
  questionText: string;
  dimensionId: string;
  dimensionName: string;
  componentId: string;
  componentName: string;
  attributeId: string;
  attributeName: string;
  attributeWeight: number;
  answerOptions: AnswerOption[];
  selectedAnswerId?: string;
  selectedScore?: number;
  assessmentStatus: string;
}

export interface AnswerOption {
  id: string;
  text: string;
  score: number;
  displayOrder: number;
}

export interface AssessmentResults {
  applicationId: string;
  totalQuestions: number;
  answeredQuestions: number;
  overallScore: number;
  completionPercentage: number;
  recommendation: string;
  dimensionScores: DimensionScore[];
}

export interface DimensionScore {
  id: string;
  name: string;
  score: number;
  questionsAnswered: number;
  totalQuestions: number;
}

interface QAAssessmentModalProps {
  applicationId: string;
  applicationName: string;
  aprRunId?: string;
  isOpen: boolean;
  onClose: () => void;
  onComplete: (results: AssessmentResults) => void;
}

const QAAssessmentModal: React.FC<QAAssessmentModalProps> = ({
  applicationId,
  applicationName,
  aprRunId,
  isOpen,
  onClose,
  onComplete
}) => {
  const [questions, setQuestions] = useState<QAQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedTab, setSelectedTab] = useState('all');
  const [answers, setAnswers] = useState<Record<string, { answerId: string; score: number }>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scores, setScores] = useState<AssessmentResults | null>(null);
  const [dimensions, setDimensions] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen && applicationId) {
      loadAssessmentData();
    }
  }, [isOpen, applicationId, aprRunId]);

  const loadAssessmentData = async () => {
    setLoading(true);
    try {
      // Load questions from qa_assessments table
      console.log('Loading QA data for application:', applicationId, 'aprRunId:', aprRunId);
      const response = await authFetch(`/api/assessments/qa/${applicationId}${aprRunId ? `?apr_run_id=${aprRunId}` : ''}`);
      if (!response.ok) throw new Error('Failed to load questions');
      
      const qaData = await response.json();
      console.log('QA Data loaded:', qaData.length, 'records');
      
      // Transform qa_assessments data into questions format
      const questionsMap: Record<string, QAQuestion> = {};
      const answersMap: Record<string, { answerId: string; score: number }> = {};
      const dimensionsSet = new Set<string>();

      for (const qa of qaData) {
        dimensionsSet.add(qa.dimension_name);
        
        if (!questionsMap[qa.question_id]) {
          // Get answer options for this question
          try {
            console.log('Loading answer options for question:', qa.question_id);
            const optionsResponse = await authFetch(`/api/questions/${qa.question_id}/answer-options`);
            const rawAnswerOptions = optionsResponse.ok ? await optionsResponse.json() : [];

            // Transform snake_case to camelCase for answer options
            const answerOptions = rawAnswerOptions.map((opt: any) => ({
              id: opt.id,
              text: opt.text,
              score: opt.score,
              displayOrder: opt.display_order || opt.displayOrder || 0
            }));

            console.log('Answer options loaded for', qa.question_id, ':', answerOptions.length, 'options');

            // Check if assessment is completed (case-insensitive)
            const isCompleted = qa.assessment_status && qa.assessment_status.toLowerCase() === 'completed';

            console.log('Question assessment status:', {
              questionId: qa.question_id,
              rawStatus: qa.assessment_status,
              isCompleted,
              answerOptionId: qa.answer_option_id,
              answerOptionsIds: answerOptions.map((o: any) => o.id)
            });

            questionsMap[qa.question_id] = {
              questionId: qa.question_id,
              questionText: qa.question_text,
              dimensionId: qa.dimension_id,
              dimensionName: qa.dimension_name,
              componentId: qa.component_id,
              componentName: qa.component_name,
              attributeId: qa.attribute_id,
              attributeName: qa.attribute_name,
              attributeWeight: qa.attribute_weight,
              answerOptions: answerOptions || [], // Ensure it's always an array
              selectedAnswerId: isCompleted ? qa.answer_option_id : undefined,
              selectedScore: isCompleted ? qa.answer_score : undefined,
              assessmentStatus: qa.assessment_status
            };
          } catch (error) {
            console.error(`Failed to load answer options for question ${qa.question_id}:`, error);
            // Create question with empty answer options as fallback
            const isCompleted = qa.assessment_status && qa.assessment_status.toLowerCase() === 'completed';
            questionsMap[qa.question_id] = {
              questionId: qa.question_id,
              questionText: qa.question_text,
              dimensionId: qa.dimension_id,
              dimensionName: qa.dimension_name,
              componentId: qa.component_id,
              componentName: qa.component_name,
              attributeId: qa.attribute_id,
              attributeName: qa.attribute_name,
              attributeWeight: qa.attribute_weight,
              answerOptions: [], // Empty fallback
              selectedAnswerId: isCompleted ? qa.answer_option_id : undefined,
              selectedScore: isCompleted ? qa.answer_score : undefined,
              assessmentStatus: qa.assessment_status
            };
          }

          // Also populate answersMap for completed questions (case-insensitive)
          if (qa.assessment_status && qa.assessment_status.toLowerCase() === 'completed') {
            answersMap[qa.question_id] = {
              answerId: qa.answer_option_id,
              score: qa.answer_score
            };
          }
        }
      }

      // Define the desired dimension order
      const dimensionOrder = [
        'Business Value & Strategic Fit',
        'Business Value',
        'Strategic Fit',
        'Business Case',
        'Application Performance',
        'Performance',
        'Application Risk Mitigation',
        'Risk Mitigation',
        'Risk',
        'Architectural Maturity',
        'Architecture'
      ];

      // Sort questions by specified dimension order, then component, then attribute
      const questionsList = Object.values(questionsMap).sort((a, b) => {
        // Get dimension order positions
        const getOrderPos = (dimName: string) => {
          const pos = dimensionOrder.findIndex(d => dimName.includes(d) || d.includes(dimName));
          return pos === -1 ? 999 : pos;
        };
        
        const dimOrderA = getOrderPos(a.dimensionName);
        const dimOrderB = getOrderPos(b.dimensionName);
        
        if (dimOrderA !== dimOrderB) return dimOrderA - dimOrderB;
        
        const compCompare = a.componentName.localeCompare(b.componentName);
        if (compCompare !== 0) return compCompare;
        return a.attributeName.localeCompare(b.attributeName);
      });

      setQuestions(questionsList);
      setAnswers(answersMap);
      
      // Sort dimensions in the same order as questions
      const sortedDimensions = Array.from(dimensionsSet).sort((a, b) => {
        const getOrderPos = (dimName: string) => {
          const pos = dimensionOrder.findIndex(d => dimName.includes(d) || d.includes(dimName));
          return pos === -1 ? 999 : pos;
        };
        return getOrderPos(a) - getOrderPos(b);
      });
      
      setDimensions(sortedDimensions);
      
      // Load current scores
      await loadScores();
      
    } catch (error) {
      console.error('Error loading assessment data:', error);
    }
    setLoading(false);
  };

  const loadScores = async () => {
    try {
      const response = await authFetch(`/api/assessments/application/${applicationId}/scores${aprRunId ? `?apr_run_id=${aprRunId}` : ''}`);
      if (response.ok) {
        const scoresData = await response.json();
        setScores(scoresData);
      }
    } catch (error) {
      console.error('Error loading scores:', error);
    }
  };

  const triggerRescoring = async () => {
    try {
      console.log('Triggering rescoring for application:', applicationId);
      await apiService.rescoreApplication(applicationId, aprRunId);
      console.log('Rescoring completed successfully');
      
      // Reload scores after rescoring
      await loadScores();
    } catch (error) {
      console.error('Error during rescoring:', error);
    }
  };

  const handleAnswerSelect = async (questionId: string, answerId: string, score: number) => {
    setSaving(true);
    try {
      // Find the question to get its weight
      const question = questions.find(q => q.questionId === questionId);
      const weight = question?.attributeWeight || 1;
      
      // Update local state
      setAnswers(prev => ({
        ...prev,
        [questionId]: { answerId, score }
      }));

      // Update question status
      setQuestions(prev => prev.map(q => 
        q.questionId === questionId 
          ? { ...q, selectedAnswerId: answerId, selectedScore: score, assessmentStatus: 'Completed' }
          : q
      ));

      // Save to database with score and weight
      await apiService.saveAssessmentResponse({
        application_id: applicationId,
        question_id: questionId,
        answer_option_id: answerId,
        apr_run_id: aprRunId,
        score: score,
        weight: weight
      });

      // Reload scores after saving
      await loadScores();

    } catch (error) {
      console.error('Error saving answer:', error);
    }
    setSaving(false);
  };

  const getFilteredQuestions = () => {
    if (selectedTab === 'all') return questions;
    return questions.filter(q => q.dimensionName === selectedTab);
  };

  const getCompletionStats = () => {
    const answered = questions.filter(q => q.assessmentStatus && q.assessmentStatus.toLowerCase() === 'completed').length;
    const total = questions.length;
    const percentage = total > 0 ? (answered / total) * 100 : 0;
    return { answered, total, percentage };
  };

  const filteredQuestions = getFilteredQuestions();
  const currentQuestion = filteredQuestions[currentQuestionIndex];
  const stats = getCompletionStats();

  // Debug logging
  useEffect(() => {
    console.log('QA Modal Debug Info:', {
      questionsCount: questions.length,
      filteredQuestionsCount: filteredQuestions.length,
      currentQuestionIndex,
      currentQuestion: currentQuestion ? {
        id: currentQuestion.questionId,
        text: currentQuestion.questionText,
        hasAnswerOptions: !!currentQuestion.answerOptions,
        answerOptionsCount: currentQuestion.answerOptions?.length || 0,
        selectedAnswerId: currentQuestion.selectedAnswerId,
        assessmentStatus: currentQuestion.assessmentStatus,
        answerOptionIds: currentQuestion.answerOptions?.map(o => o.id) || []
      } : null
    });

    // Extra debug for answer matching
    if (currentQuestion && currentQuestion.selectedAnswerId && currentQuestion.answerOptions) {
      const matchFound = currentQuestion.answerOptions.some(o => o.id === currentQuestion.selectedAnswerId);
      console.log('Answer ID Match Check:', {
        selectedAnswerId: currentQuestion.selectedAnswerId,
        selectedAnswerIdType: typeof currentQuestion.selectedAnswerId,
        optionIds: currentQuestion.answerOptions.map(o => ({ id: o.id, type: typeof o.id })),
        matchFound
      });
    }
  }, [questions, filteredQuestions, currentQuestionIndex, currentQuestion]);

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="md-dialog md-dialog--open">
        <div className="md-dialog__backdrop" onClick={onClose}></div>
        <div className="md-dialog__container">
          <div className="md-dialog__content">
            <div className="loading-spinner">Loading Q&A Assessment...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '0',
          width: '90%',
          maxWidth: '900px',
          maxHeight: '90vh',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.15)',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}>
          <div>
            <h2 style={{
              margin: '0 0 8px 0',
              fontSize: '24px',
              fontWeight: '500',
              color: '#1a1a1a'
            }}>
              Application Assessment
            </h2>
            <p style={{
              margin: 0,
              fontSize: '14px',
              color: '#666'
            }}>
              {applicationName}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '4px 8px',
              borderRadius: '4px'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            ×
          </button>
        </div>

        {/* Progress Indicator */}
        <div style={{
          padding: '16px 24px',
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <span style={{ fontSize: '12px', fontWeight: '500', textTransform: 'uppercase', color: '#666' }}>Progress</span>
            <span style={{ fontSize: '14px', color: '#333' }}>{stats.answered} of {stats.total} questions</span>
            <span style={{ fontSize: '14px', fontWeight: '500', color: '#1976d2' }}>{stats.percentage.toFixed(0)}%</span>
          </div>
          <div style={{
            height: '4px',
            backgroundColor: '#e0e0e0',
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              backgroundColor: '#1976d2',
              borderRadius: '2px',
              width: `${stats.percentage}%`,
              transition: 'width 0.3s ease'
            }}></div>
          </div>
          {saving && <div style={{ marginTop: '8px', fontSize: '12px', color: '#f57c00', fontWeight: '500' }}>Saving...</div>}
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '4px',
          padding: '12px 24px',
          borderBottom: '1px solid #e0e0e0',
          overflowX: 'auto',
          backgroundColor: 'white'
        }}>
          <button
            onClick={() => { setSelectedTab('all'); setCurrentQuestionIndex(0); }}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '20px',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              backgroundColor: selectedTab === 'all' ? '#1976d2' : '#f0f0f0',
              color: selectedTab === 'all' ? 'white' : '#666',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s'
            }}
          >
            All Dimensions
          </button>
          {dimensions.map(dim => (
            <button
              key={dim}
              onClick={() => { setSelectedTab(dim); setCurrentQuestionIndex(0); }}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '20px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                backgroundColor: selectedTab === dim ? '#1976d2' : '#f0f0f0',
                color: selectedTab === dim ? 'white' : '#666',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s'
              }}
            >
              {dim}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px',
          backgroundColor: '#fafafa'
        }}>
          {currentQuestion && currentQuestion.answerOptions ? (
            <div style={{ maxWidth: '700px', margin: '0 auto' }}>
              {/* Breadcrumb */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '12px',
                fontSize: '12px',
                color: '#666'
              }}>
                <span>{currentQuestion.dimensionName}</span>
                <span style={{ margin: '0 8px', color: '#999' }}>›</span>
                <span>{currentQuestion.componentName}</span>
                <span style={{ margin: '0 8px', color: '#999' }}>›</span>
                <span style={{ color: '#333', fontWeight: '500' }}>{currentQuestion.attributeName}</span>
              </div>

              {/* Question Card */}
              <div style={{
                background: 'white',
                borderRadius: '8px',
                padding: '20px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                border: '1px solid #e0e0e0'
              }}>
                <div style={{ marginBottom: '12px' }}>
                  <span style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    backgroundColor: '#e3f2fd',
                    color: '#1976d2',
                    borderRadius: '16px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    Question {currentQuestionIndex + 1}
                  </span>
                </div>
                <h3 style={{
                  margin: '0 0 16px 0',
                  fontSize: '16px',
                  fontWeight: '400',
                  color: '#333',
                  lineHeight: '1.4'
                }}>
                  {currentQuestion.questionText}
                </h3>

                {/* Answer Options */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {(currentQuestion.answerOptions || [])
                    .sort((a, b) => a.displayOrder - b.displayOrder)
                    .map(option => (
                      <label
                        key={option.id}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          padding: '12px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          backgroundColor: currentQuestion.selectedAnswerId === option.id ? '#e3f2fd' : 'transparent',
                          border: currentQuestion.selectedAnswerId === option.id ? '2px solid #1976d2' : '2px solid transparent',
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => {
                          if (currentQuestion.selectedAnswerId !== option.id) {
                            e.currentTarget.style.backgroundColor = '#f5f5f5';
                          }
                        }}
                        onMouseOut={(e) => {
                          if (currentQuestion.selectedAnswerId !== option.id) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <input
                          type="radio"
                          name={`question-${currentQuestion.questionId}`}
                          value={option.id}
                          checked={currentQuestion.selectedAnswerId === option.id}
                          onChange={() => handleAnswerSelect(currentQuestion.questionId, option.id, option.score)}
                          style={{ marginRight: '12px', marginTop: '2px' }}
                        />
                        <span style={{ fontSize: '14px', color: '#333', lineHeight: '1.4' }}>
                          {option.text}
                          {currentQuestion.selectedAnswerId === option.id && (
                            <span style={{
                              marginLeft: '8px',
                              padding: '2px 8px',
                              backgroundColor: '#1976d2',
                              color: 'white',
                              borderRadius: '10px',
                              fontSize: '11px',
                              fontWeight: '500'
                            }}>
                              Selected
                            </span>
                          )}
                        </span>
                      </label>
                    ))}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '48px 24px', color: '#666' }}>
              {!currentQuestion
                ? (selectedTab === 'all'
                    ? 'No questions available for this application.'
                    : `No questions available for ${selectedTab} dimension.`
                  )
                : 'Loading answer options for this question...'
              }
              {currentQuestion && !currentQuestion.answerOptions && (
                <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#dc3545' }}>
                  Failed to load answer options for question: {currentQuestion.questionText}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #e0e0e0',
          backgroundColor: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))}
              disabled={currentQuestionIndex === 0}
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '500',
                color: currentQuestionIndex === 0 ? '#ccc' : '#1976d2',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: '6px',
                cursor: currentQuestionIndex === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              ← Previous
            </button>
            <span style={{ fontSize: '14px', fontWeight: '500', color: '#666' }}>
              {currentQuestionIndex + 1} / {getFilteredQuestions().length}
            </span>
            <button
              onClick={() => setCurrentQuestionIndex(Math.min(getFilteredQuestions().length - 1, currentQuestionIndex + 1))}
              disabled={currentQuestionIndex >= getFilteredQuestions().length - 1}
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '500',
                color: currentQuestionIndex >= getFilteredQuestions().length - 1 ? '#ccc' : '#1976d2',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: '6px',
                cursor: currentQuestionIndex >= getFilteredQuestions().length - 1 ? 'not-allowed' : 'pointer'
              }}
            >
              Next →
            </button>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={async () => {
                await triggerRescoring();
              }}
              style={{
                padding: '10px 24px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#666',
                backgroundColor: 'transparent',
                border: '1px solid #ddd',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              Save
            </button>
            <button
              onClick={async () => {
                await triggerRescoring();
                onClose();
              }}
              style={{
                padding: '10px 24px',
                fontSize: '14px',
                fontWeight: '500',
                color: 'white',
                backgroundColor: '#1976d2',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#1565c0'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#1976d2'}
            >
              Save & Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QAAssessmentModal;